import { useState } from "react";
import { Link } from "react-router-dom";
import { Play, Square, Trash2, RotateCw, ExternalLink } from "lucide-react";
import { clsx } from "clsx";
import type { VM, VMStatus } from "@/types";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";
import { DeleteVMConfirmModal } from "@/components/vm/DeleteVMConfirmModal";

interface VMListProps {
  vms: VM[];
  isLoading: boolean;
  onStart: (vmId: number) => void;
  onStop: (vmId: number) => void;
  onRestart: (vmId: number) => void;
  onDelete: (vmId: number) => void;
  // Provided only when the current user has admin scope (super-admin
  // sees VMs across tenants — see backend list_vms patch). Adds a
  // "Tenant" column so admin can tell rows apart at a glance.
  tenantsById?: Map<number, { slug: string; name: string }>;
}

const statusColors: Record<VMStatus, string> = {
  running: "bg-green-500/20 text-green-400",
  stopped: "bg-gray-500/20 text-gray-400",
  paused: "bg-yellow-500/20 text-yellow-400",
  suspended: "bg-orange-500/20 text-orange-400",
  migrating: "bg-blue-500/20 text-blue-400",
  error: "bg-red-500/20 text-red-400",
  provisioning: "bg-purple-500/20 text-purple-400",
};

const statusDotColors: Record<VMStatus, string> = {
  running: "bg-green-400",
  stopped: "bg-gray-400",
  paused: "bg-yellow-400",
  suspended: "bg-orange-400",
  migrating: "bg-blue-400",
  error: "bg-red-400",
  provisioning: "bg-purple-400",
};

export function VMList({
  vms,
  isLoading,
  onStart,
  onStop,
  onRestart,
  onDelete,
  tenantsById,
}: VMListProps) {
  const showTenantColumn = !!tenantsById;
  const [deleteTarget, setDeleteTarget] = useState<VM | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (vms.length === 0) {
    return (
      <div className="py-20 text-center">
        <p className="text-lg text-gray-400">No virtual machines found.</p>
        <p className="mt-1 text-sm text-gray-500">
          Create a new VM from the Provisioning page.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto rounded-xl border border-gray-800">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-800 bg-gray-900/50">
              <th className="px-4 py-3 text-left font-medium text-gray-400">
                Name
              </th>
              {showTenantColumn && (
                <th className="px-4 py-3 text-left font-medium text-gray-400">
                  Tenant
                </th>
              )}
              <th className="px-4 py-3 text-left font-medium text-gray-400">
                Status
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-400">
                Node
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-400">
                Tier
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-400">
                CPU
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-400">
                RAM
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-400">
                IP Address
              </th>
              <th className="px-4 py-3 text-right font-medium text-gray-400">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {vms.map((vm) => (
              <tr
                key={vm.id}
                className="transition-colors hover:bg-gray-900/30"
              >
                <td className="px-4 py-3">
                  <Link
                    to={`/vms/${vm.id}`}
                    className="flex items-center gap-2 font-medium text-gray-200 hover:text-blue-400"
                  >
                    {vm.name}
                    <ExternalLink className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
                  </Link>
                  <span className="text-xs text-gray-500">
                    VMID: {vm.vmid}
                  </span>
                </td>
                {showTenantColumn && (
                  <td className="px-4 py-3">
                    {(() => {
                      const t = tenantsById!.get(vm.tenant_id);
                      return t ? (
                        <span className="rounded bg-blue-500/10 px-2 py-0.5 text-xs font-medium text-blue-300" title={t.name}>
                          {t.slug}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-500">#{vm.tenant_id}</span>
                      );
                    })()}
                  </td>
                )}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <span
                      className={clsx(
                        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
                        statusColors[vm.status]
                      )}
                    >
                      <span
                        className={clsx(
                          "h-1.5 w-1.5 rounded-full",
                          statusDotColors[vm.status]
                        )}
                      />
                      {vm.status}
                    </span>
                    {vm.omer_enabled && (
                      <span className="rounded-full bg-violet-500/20 px-1.5 py-0.5 text-[10px] font-medium text-violet-400" title="OMER AI active">
                        AI
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-300">{vm.node}</td>
                <td className="px-4 py-3">
                  <span className="rounded bg-gray-800 px-2 py-0.5 text-xs font-medium text-gray-300">
                    Tier {vm.service_tier}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-300">
                  {vm.cores} cores
                </td>
                <td className="px-4 py-3 text-gray-300">
                  {vm.memory_mb >= 1024
                    ? `${(vm.memory_mb / 1024).toFixed(1)} GB`
                    : `${vm.memory_mb} MB`}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-gray-300">
                  {vm.ip_address ?? "-"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {vm.status === "stopped" && (
                      <button
                        onClick={() => onStart(vm.id)}
                        title="Start"
                        className="rounded-lg p-1.5 text-green-400 transition-colors hover:bg-green-500/10"
                      >
                        <Play className="h-4 w-4" />
                      </button>
                    )}
                    {vm.status === "running" && (
                      <>
                        <button
                          onClick={() => onStop(vm.id)}
                          title="Stop"
                          className="rounded-lg p-1.5 text-yellow-400 transition-colors hover:bg-yellow-500/10"
                        >
                          <Square className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => onRestart(vm.id)}
                          title="Restart"
                          className="rounded-lg p-1.5 text-blue-400 transition-colors hover:bg-blue-500/10"
                        >
                          <RotateCw className="h-4 w-4" />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => setDeleteTarget(vm)}
                      title="Delete"
                      className="rounded-lg p-1.5 text-red-400 transition-colors hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <DeleteVMConfirmModal
        open={!!deleteTarget}
        vmName={deleteTarget?.name ?? ""}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) {
            onDelete(deleteTarget.id);
            setDeleteTarget(null);
          }
        }}
      />
    </>
  );
}
